import React, { useEffect, useState } from 'react';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { useTranslation } from 'react-i18next';
import { HOME_ROUTE } from '../../../lib/constants/route';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import '../../../styles/pages/settings/settingsCommunication.scss';
import {
  linkSVG,
  messageSVG,
  phoneSVG,
  questionSVG,
  securitySVG,
  slackSVG,
  tickWithCircleSVG,
  whatsappSVG,
} from '../../../lib/allCustomSVGs';
import AIPhoneInput from '../../../ui/inputs/AIPhoneInput';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import getCountryByDialCode from '../../../lib/helper-functions/getCountryByDialCode';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { submitLogToBugsnag } from '../../../lib/api/log';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import Popup from '../../../ui/popup/Popup';
import { OTPCodeInput } from '../../../ui/inputs/OTPCodeInput';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import { CircularProgress } from '@mui/material';
import get_communication_channels_api_call from '../../../lib/api/settings/communicationSettings/get_communication_channels_api_call';
import create_communication_channel_api_call from '../../../lib/api/settings/communicationSettings/create_communication_channel_api_call';
import update_communication_channel_api_call from '../../../lib/api/settings/communicationSettings/update_communication_channel_api_call';
import request_channel_verification_code_api_call from '../../../lib/api/settings/communicationSettings/request_channel_verification_code_api_call';
import HELP_LINKS from '../../../lib/constants/helpLinks';

const CommunicationSettings = () => {
  const { t } = useTranslation();

  // redux state
  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const isWhiteLabel = useSelector(
    (state: RootState) => state.UI?.isWhiteLabel
  );
  const countries = useSelector(
    (state: RootState) => state.app.countries.available
  );
  const IPCountry = useSelector(
    (state: RootState) => state.app.countries.countryOfIP
  );
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const userRole: 'seller' | 'agency' =
    useSelector((state: RootState) => state.user?.profile?.role) === 'agency'
      ? 'agency'
      : 'seller';

  // local state
  const [loading, setLoading] = useState<'pending' | 'success' | 'error'>(
    'pending'
  );
  const [allUserChannels, setAllUserChannels] = useState<any[]>([]);
  let sellerToken: string;
  if (isWhiteLabel) {
    sellerToken = accessToken?.currentWhiteLabelSellerToken;
  } else {
    sellerToken = accessToken?.sellerToken;
  }

  const [whatsAppNumber, setWhatsAppNumber] = useState({
    number: '',
    country: userProfile.country_code
      ? getCountryByDialCode(userProfile.country_code)
      : countries[IPCountry],
  });
  const [contactNumber, setContactNumber] = useState({
    number: '',
    country: userProfile.country_code
      ? getCountryByDialCode(userProfile.country_code)
      : countries[IPCountry],
  });

  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [isDataSubmitting, setIsDataSubmitting] = useState(false);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSlackVerified, setIsSlackVerified] = useState(false);

  // track OTP lifecycle and active action
  const [activeMode, setActiveMode] = useState<'create' | 'update' | null>(
    null
  );
  const [activeChannelType, setActiveChannelType] = useState<
    'whatsapp' | 'primary_email' | 'secondary_email' | 'contact_number' | null
  >(null);
  const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);
  const [isOtpSuccess, setIsOtpSuccess] = useState(false);
  const [otpInputResetKey, setOtpInputResetKey] = useState<string>('');

  const togglePopupModal = (isOpen: boolean) => {
    setIsPopupOpen(isOpen);
    if (!isOpen) {
      // reset OTP UI state when closing popup
      setIsOtpSubmitting(false);
      setIsOtpSuccess(false);
      setActiveMode(null);
      setActiveChannelType(null);
      // trigger a fresh OTP input
      setOtpInputResetKey(`${Date.now()}`);
    }
  };

  const [whatsappChannel, setWhatsappChannel] = useState<any>(null);
  const [primaryEmailChannel, setPrimaryEmailChannel] = useState<any>(null);
  const [secondaryEmailChannel, setSecondaryEmailChannel] = useState<any>(null);
  const [contactNumberChannel, setContactNumberChannel] = useState<any>(null);
  const [slackChannel, setSlackChannel] = useState<any>(null);

  // Helper to remove the dial code prefix from a stored raw value
  const stripDialCode = (rawVal?: string, dialCodeWithPlus?: string) => {
    if (!rawVal) return '';
    const dialDigits = (dialCodeWithPlus || '').replace('+', '');
    let v = rawVal.startsWith('+') ? rawVal.slice(1) : rawVal; // drop leading '+'
    // If raw starts with the dial code digits, remove that prefix
    if (dialDigits && v.startsWith(dialDigits)) {
      return v.slice(dialDigits.length);
    }
    return v; // fallback: return without leading '+'
  };

  /* ----------------------------API CALLS START---------------------------- */
  async function getUserChannels() {
    setLoading('pending');
    try {
      const res: any = await get_communication_channels_api_call();

      if (res && res?.channels) {
        setLoading('success');

        if (res?.channels?.length > 0) {
          setAllUserChannels(res?.channels);
          console.log(res?.channels);

          const whatsappChannelData = res.channels.find(
            (channel: any) => channel.channel_type === 'whatsapp'
          );

          const primaryEmailChannelData = res.channels.find(
            (channel: any) => channel.channel_type === 'primary_email'
          );

          const secondaryEmailChannelData = res.channels.find(
            (channel: any) => channel.channel_type === 'secondary_email'
          );

          const contactNumberChannelData = res.channels.find(
            (channel: any) => channel.channel_type === 'contact_number'
          );

          const slackChannelData = res.channels.find(
            (channel: any) => channel.channel_type === 'slack'
          );

          if (whatsappChannelData) {
            setWhatsappChannel(whatsappChannelData);
            //remove dial code from saved number
            const dial = whatsappChannelData?.metadata?.dial_code; // e.g. "+94"
            const localNum = stripDialCode(
              whatsappChannelData.raw_channel_value,
              dial
            ); // NEW
            setWhatsAppNumber({
              number: localNum, // NEW: store only the local number without dial code
              country: getCountryByDialCode((dial || '').replace('+', '')),
            });
          } else {
            setWhatsappChannel(null);
          }

          if (primaryEmailChannelData) {
            setPrimaryEmailChannel(primaryEmailChannelData);
            setPrimaryEmail(primaryEmailChannelData.raw_channel_value);
          } else {
            setPrimaryEmailChannel(null);
            setPrimaryEmail('');
          }

          if (secondaryEmailChannelData) {
            setSecondaryEmailChannel(secondaryEmailChannelData);
            setSecondaryEmail(secondaryEmailChannelData.raw_channel_value);
          } else {
            setSecondaryEmailChannel(null);
            setSecondaryEmail('');
          }

          if (contactNumberChannelData) {
            setContactNumberChannel(contactNumberChannelData);
            // remove dial code from saved number
            const dial = contactNumberChannelData?.metadata?.dial_code; // e.g. "+94"
            const localNum = stripDialCode(
              contactNumberChannelData.raw_channel_value,
              dial
            ); // NEW
            setContactNumber({
              number: localNum, // NEW: store only the local number without dial code
              country: getCountryByDialCode((dial || '').replace('+', '')),
            });
          } else {
            setContactNumberChannel(null);
          }

          if (slackChannelData) {
            setSlackChannel(slackChannelData);
          } else {
            setSlackChannel(null);
          }
        } else {
          setAllUserChannels([]);
          setWhatsAppNumber({
            number: '',
            country: userProfile.country_code
              ? getCountryByDialCode(userProfile.country_code)
              : countries[IPCountry],
          });
          setPrimaryEmail('');
          setSecondaryEmail('');
          setContactNumber({
            number: '',
            country: userProfile.country_code
              ? getCountryByDialCode(userProfile.country_code)
              : countries[IPCountry],
          });
          setWhatsappChannel(null);
          setPrimaryEmailChannel(null);
          setSecondaryEmailChannel(null);
          setContactNumberChannel(null);
          setSlackChannel(null);
        }
      } else {
        setLoading('error');
      }
    } catch (exception) {
      setLoading('error');
      console.log('Error during request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  const requestChannelVerification = async (channelData: any) => {
    // this only requests the OTP and opens the OTP popup
    setIsDataSubmitting(true);
    togglePopupModal(true);
    const res: any = await request_channel_verification_code_api_call(
      channelData
    );
    if (res) {
      setIsDataSubmitting(false);
      // popup stays open for OTP entry
    } else {
      setIsDataSubmitting(false);
      togglePopupModal(false);
    }
  };

  const createChannel = async (channelData: any) => {
    setIsDataSubmitting(true);
    const res: any = await create_communication_channel_api_call(channelData);
    if (res) {
      setIsDataSubmitting(false);
      // show success, then close and refresh
      setIsOtpSuccess(true);
      setTimeout(() => {
        togglePopupModal(false);
        getUserChannels();
      }, 800);
    } else {
      setIsDataSubmitting(false);
      togglePopupModal(false);
    }
  };

  const updateChannel = async (channelId: string, channelData: any) => {
    setIsDataSubmitting(true);
    const res: any = await update_communication_channel_api_call(
      channelData,
      channelId
    );
    if (res) {
      setIsDataSubmitting(false);
      // show success, then close and refresh
      setIsOtpSuccess(true);
      setTimeout(() => {
        togglePopupModal(false);
        getUserChannels();
      }, 800);
    } else {
      setIsDataSubmitting(false);
      togglePopupModal(false);
    }
  };
  /* ----------------------------API CALLS END---------------------------- */

  // Build payloads. We pass verification_code explicitly at submit time.
  const getSubmittingChannelData = (
    channel_type:
      | 'whatsapp'
      | 'primary_email'
      | 'secondary_email'
      | 'contact_number'
      | 'slack',
    verification_code?: string
  ) => {
    let channelData: any = {};

    if (channel_type === 'whatsapp') {
      channelData = {
        channel_type: 'whatsapp',
        channel_value: `${whatsAppNumber?.country?.dialCode}${whatsAppNumber?.number}`,
        ...(verification_code ? { verification_code } : {}),
        metadata: {
          dial_code: whatsAppNumber?.country?.dialCode,
        },
      };
    } else if (channel_type === 'primary_email') {
      channelData = {
        channel_type: 'primary_email',
        channel_value: primaryEmail,
        ...(verification_code ? { verification_code } : {}),
      };
    } else if (channel_type === 'secondary_email') {
      channelData = {
        channel_type: 'secondary_email',
        channel_value: secondaryEmail,
        ...(verification_code ? { verification_code } : {}),
      };
    } else if (channel_type === 'contact_number') {
      channelData = {
        channel_type: 'contact_number',
        channel_value: `${contactNumber?.country?.dialCode}${contactNumber?.number}`,
        ...(verification_code ? { verification_code } : {}),
        metadata: {
          dial_code: contactNumber?.country?.dialCode,
        },
      };
    } else if (channel_type === 'slack') {
      // Slack doesn't use OTP. Included for completeness.
      channelData = {
        channel_type: 'slack',
        channel_value: slackChannel,
        is_verified: isSlackVerified,
      };
    }

    return channelData;
  };

  const getSubmittingVerificationData = (channel_type: string) => {
    let verificationData: any = {};

    if (channel_type === 'whatsapp') {
      verificationData = {
        channel_type: 'whatsapp',
        channel_value: `${whatsAppNumber?.country?.dialCode}${whatsAppNumber?.number}`,
      };
    } else if (channel_type === 'primary_email') {
      verificationData = {
        channel_type: 'primary_email',
        channel_value: primaryEmail,
      };
    } else if (channel_type === 'secondary_email') {
      verificationData = {
        channel_type: 'secondary_email',
        channel_value: secondaryEmail,
      };
    } else if (channel_type === 'contact_number') {
      verificationData = {
        channel_type: 'contact_number',
        channel_value: `${contactNumber?.country?.dialCode}${contactNumber?.number}`,
      };
    }

    return verificationData;
  };

  // helper to kick off the verification flow for create/update
  const handleStartVerificationFlow = async (
    mode: 'create' | 'update',
    channelType:
      | 'whatsapp'
      | 'primary_email'
      | 'secondary_email'
      | 'contact_number',
    existingChannelId?: string
  ) => {
    try {
      setActiveMode(mode);
      setActiveChannelType(channelType);
      setOtpInputResetKey(`${Date.now()}`); // reset OTP input for fresh entry

      // 1) Request OTP (opens popup inside)
      await requestChannelVerification(
        getSubmittingVerificationData(channelType)
      );
      // 2) User will type OTP; onComplete handler will submit create/update
    } catch (e) {
      submitLogToBugsnag('error', `Verification flow start failed: ${e}`);
      // close popup if error happened during request
      togglePopupModal(false);
      setActiveMode(null);
      setActiveChannelType(null);
    }
  };

  // when OTP is complete, auto-submit the pending create/update action
  const handleOtpComplete = async (code: string) => {
    if (!activeChannelType || !activeMode) return;

    try {
      setIsOtpSubmitting(true);

      const payload = getSubmittingChannelData(activeChannelType, code);

      if (activeMode === 'create') {
        await createChannel(payload);
      } else {
        // resolve the correct channel id for update
        let channelId: string | undefined;
        if (activeChannelType === 'whatsapp') channelId = whatsappChannel?.id;
        if (activeChannelType === 'primary_email')
          channelId = primaryEmailChannel?.id;
        if (activeChannelType === 'secondary_email')
          channelId = secondaryEmailChannel?.id;
        if (activeChannelType === 'contact_number')
          channelId = contactNumberChannel?.id;

        if (!channelId) {
          // No existing channel means fallback to create (just in case)
          await createChannel(payload);
        } else {
          await updateChannel(channelId, payload);
        }
      }
    } catch (e) {
      submitLogToBugsnag('error', `OTP submit failed: ${e}`);
      // leave popup open to allow retry (user can resend from the page if you add it later)
      setIsOtpSubmitting(false);
    } finally {
      // createChannel / updateChannel will flip success state & close popup
      setIsOtpSubmitting(false);
    }
  };

  useEffect(() => {
    getUserChannels();
  }, []);

  const hasWhatsappInput = Boolean(whatsAppNumber?.number);
  const hasContactInput = Boolean(contactNumber?.number);
  const hasPrimaryEmailInput = Boolean(primaryEmail);
  const hasSecondaryEmailInput = Boolean(secondaryEmail);

  const primaryEmailBtnLabel = () => {
    if (primaryEmailChannel === null) {
      return t('Create');
    } else if (
      primaryEmailChannel &&
      primaryEmailChannel?.verification_status?.status === 'pending'
    ) {
      return t('Verify');
    } else if (
      primaryEmailChannel &&
      primaryEmailChannel?.raw_channel_value !== ''
    ) {
      return t('Update');
    } else {
      return t('Create');
    }
  };

  const secondaryEmailBtnLabel = () => {
    if (secondaryEmailChannel === null) {
      return t('Create');
    } else if (
      secondaryEmailChannel &&
      secondaryEmailChannel?.verification_status?.status === 'pending'
    ) {
      return t('Verify');
    } else if (
      secondaryEmailChannel &&
      secondaryEmailChannel?.raw_channel_value !== ''
    ) {
      return t('Update');
    } else {
      return t('Create');
    }
  };

  return (
    <SubPageInnerContentHolder
      title={t('Communication')}
      backBtnLink={HOME_ROUTE}
      /* ------Help Button------ */
      mainButtonType='white'
      mainBtnTitle={t('Help')}
      mainBtnIcon={questionSVG}
      mainBtnLoading={false}
      mainBtnLink={HELP_LINKS.settings.communication}
      isMainBtnLinkExternal={true}
    >
      <>
        {loading === 'pending' && <LoadingSpinner />}
        {loading === 'error' && (
          <NoData
            title={t('ErrorFetchingData')}
            subTitle={t('SomethingWentWrong-msg')}
          />
        )}

        {loading === 'success' && (
          <div className='settingsCommunication'>
            <SettingsCommonCard
              mainTitle={t('CommunicationChannelsTitle')}
              subTitle={t('CommunicationChannelsSubtitle')}
            />

            {/* --------------------- WhatsApp Section Start --------------------- */}

            {userRole !== 'agency' && (
              <SettingsCommonCard
                mainIconSvg={whatsappSVG}
                mainIconBgColor='var(--card-green-background-color)'
                mainIconSvgFillColor='#16a34a'
                mainTitle={t('WhatsAppIntegrationTitle')}
                subTitle={t('WhatsAppIntegrationSubtitle')}
              >
                <div className='settingsCommunication__whatsappIntegration'>
                  <div className='settingsCommunication__singleInput'>
                    <AIPhoneInput
                      id='communicationSettings-whatsapp-input'
                      setPhoneNumber={setWhatsAppNumber}
                      phoneNumber={whatsAppNumber}
                      label={t('WhatsAppNumber')}
                      placeholder={t('EnterWhatsAppNumber')}
                      title={t('Phone-validation')}
                      required={true}
                      pattern='^[0-9]{4,}$'
                    />
                  </div>

                  <div className='settingsCommunication__buttons'>
                    <MainSquareButton
                      value={whatsappChannel ? t('Update') : t('Create')}
                      disabled={isDataSubmitting || !hasWhatsappInput}
                      type='button'
                      loading={isDataSubmitting}
                      onClick={() =>
                        handleStartVerificationFlow(
                          whatsappChannel ? 'update' : 'create',
                          'whatsapp'
                        )
                      }
                    />
                  </div>
                </div>
              </SettingsCommonCard>
            )}

            {/* --------------------- WhatsApp Section End --------------------- */}

            {/* --------------------- Contact Number Section Start --------------------- */}

            <SettingsCommonCard
              mainIconSvg={phoneSVG}
              mainIconBgColor='var(--card-orange-background-color)'
              mainIconSvgFillColor='var(--card-orange-text-color-light)'
              mainTitle={t('ContactNumberIntegrationTitle')}
              subTitle={t('ContactNumberIntegrationSubtitle')}
            >
              <div className='settingsCommunication__whatsappIntegration'>
                <div className='settingsCommunication__singleInput'>
                  <AIPhoneInput
                    id='communicationSettings-contact-input'
                    setPhoneNumber={setContactNumber}
                    phoneNumber={contactNumber}
                    label={t('ContactNumber')}
                    placeholder={t('EnterContactNumber')}
                    title={t('Phone-validation')}
                    required={true}
                    pattern='^[0-9]{4,}$'
                  />
                </div>

                <div className='settingsCommunication__buttons'>
                  <MainSquareButton
                    value={contactNumberChannel ? t('Update') : t('Create')}
                    disabled={isDataSubmitting || !hasContactInput}
                    type='button'
                    loading={isDataSubmitting}
                    onClick={() =>
                      handleStartVerificationFlow(
                        contactNumberChannel ? 'update' : 'create',
                        'contact_number'
                      )
                    }
                  />
                </div>
              </div>
            </SettingsCommonCard>

            {/* --------------------- Contact Number Section End --------------------- */}

            {/* --------------------- Email Section Start --------------------- */}

            <SettingsCommonCard
              mainIconSvg={messageSVG}
              mainIconBgColor='var(--card-blue-background-color)'
              mainIconSvgFillColor='#2563eb'
              mainTitle={t('EmailAddressesTitle')}
              subTitle={t('EmailAddressesSubtitle')}
            >
              <div className='settingsCommunication__singleInputWithBtn'>
                <div className='settingsCommunication__singleInputWithBtn--input'>
                  <AINormalInput
                    changeListeners={[(e) => setPrimaryEmail(e.target.value)]}
                    id='communicationSettings-primary-email-input'
                    value={primaryEmail}
                    placeholder={t('PrimaryEmail')}
                    required={true}
                    // title={t('Email-validation')}
                    innerPlaceholder={t('primary@email.com')}
                    inputHoverTitle={
                      primaryEmailChannel?.verification_status?.status ===
                      'pending'
                        ? t('VerificationRequired')
                        : ''
                    }
                    shouldVerify={
                      primaryEmailChannel?.verification_status?.status ===
                      'pending'
                    }
                  />
                </div>
                <div className='settingsCommunication__singleInputWithBtn--btn'>
                  <MainSquareButton
                    value={primaryEmailBtnLabel()}
                    disabled={!hasPrimaryEmailInput}
                    type='button'
                    loading={isDataSubmitting}
                    buttonType='white'
                    onClick={() =>
                      handleStartVerificationFlow(
                        primaryEmailChannel ? 'update' : 'create',
                        'primary_email'
                      )
                    }
                  />
                </div>
              </div>
              {/* --------------------------- */}
              <div className='settingsCommunication__singleInputWithBtn'>
                <div className='settingsCommunication__singleInputWithBtn--input'>
                  <AINormalInput
                    changeListeners={[(e) => setSecondaryEmail(e.target.value)]}
                    id='communicationSettings-secondary-email-input'
                    value={secondaryEmail}
                    placeholder={t('AdditionalEmail')}
                    inputHoverTitle={
                      secondaryEmailChannel?.verification_status?.status ===
                      'pending'
                        ? t('VerificationRequired')
                        : ''
                    }
                    shouldVerify={
                      secondaryEmailChannel?.verification_status?.status ===
                      'pending'
                    }
                    required={true}
                    title={t('Email-validation')}
                    innerPlaceholder={t('additional@email.com')}
                  />
                </div>
                <div className='settingsCommunication__singleInputWithBtn--btn'>
                  <MainSquareButton
                    disabled={!hasSecondaryEmailInput}
                    type='button'
                    loading={isDataSubmitting}
                    buttonType='white'
                    value={secondaryEmailBtnLabel()}
                    onClick={() =>
                      handleStartVerificationFlow(
                        secondaryEmailChannel ? 'update' : 'create',
                        'secondary_email'
                      )
                    }
                  />
                </div>
              </div>
            </SettingsCommonCard>

            {/* --------------------- Email Section End --------------------- */}

            {/* --------------------- Slack Section Start --------------------- */}

            <SettingsCommonCard
              mainIconSvg={slackSVG}
              mainIconBgColor='var(--card-purple-background-color)'
              mainIconSvgFillColor='#9333ea'
              mainTitle={t('SlackIntegrationTitle')}
              subTitle={t('SlackIntegrationSubtitle')}
            >
              <div className='settingsCommunication__slackIntegration'>
                <div className='settingsCommunication__buttons'>
                  <MainSquareButton
                    value={t('ConnectToSlack')}
                    icon={linkSVG}
                    disabled={false}
                    type='button'
                    buttonType='purple'
                    onClick={(e) => {
                      e.preventDefault();
                      const url = `https://slackapi.adup.io/slack/install`;
                      window.open(url, '_blank');
                    }}
                  />
                </div>
              </div>
            </SettingsCommonCard>

            {/* --------------------- Slack Section End --------------------- */}
          </div>
        )}

        <Popup
          styles={{ backgroundColor: '#fff' }}
          setTrigger={() => togglePopupModal(false)}
          trigger={isPopupOpen}
        >
          <div className='settingsCommunication__otpWindow'>
            <div className='settingsCommunication__otpWindow--title-container'>
              <div className='settingsCommunication__otpWindow--title'>
                <div className='settingsCommunication__otpWindow--title-logo'>
                  <CustomSVGs svg={securitySVG} />
                </div>

                <h2>{t('Verification')}</h2>
              </div>

              <div className='settingsCommunication__otpWindow--subtitle'>
                <span>
                  {t('PleaseEnterTheVerificationCodeWhichWasSentToYou')}
                </span>
              </div>
            </div>

            <div className='settingsCommunication__otpWindow--input'>
              <OTPCodeInput
                key={otpInputResetKey} // resets input on each flow
                numberOfFields={6}
                onComplete={(code: string) => {
                  //as soon as OTP completes, submit create/update
                  handleOtpComplete(code);
                }}
              />
            </div>

            {/* show verifying spinner while submitting */}
            {isOtpSubmitting && (
              <div className='settingsCommunication__otpWindow--verifying'>
                <CircularProgress size={'5rem'} />
                <span>{t('Verifying')}...</span>
              </div>
            )}

            {/* show success icon when done */}
            {isOtpSuccess && (
              <div className='settingsCommunication__otpWindow--success'>
                <CustomSVGs svg={tickWithCircleSVG} />
              </div>
            )}
          </div>
        </Popup>
      </>
    </SubPageInnerContentHolder>
  );
};

export default CommunicationSettings;
